import {
  ApolloQueryResult,
  useApolloClient,
  useMutation
} from '@apollo/client';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  IHeadCellAdvanceMetrics,
  initHeadCells,
  initHeadCellsOutreach,
  reducerUpdateAdvanceMetric,
  reducerUpdateAllLoadingComparison,
  reducerUpdateBrandPosition,
  reducerUpdateComparisonEntity,
  reducerUpdateComparisonList,
  reducerUpdateDayToDayEngagement,
  reducerUpdateDayToDayFollowersGrowth,
  reducerUpdateDayToDayTalk,
  reducerUpdateListOfPostMade,
  reducerUpdateLoadingListOfPostMade,
  reducerUpdatePeakTime,
  reducerUpdateSentimentAnalysis,
  reducerUpdateShareOfViews,
  reducerUpdateShareOfVoice
} from 'src/features/sosmedComparison';
import {
  ADD_COMPARISON,
  DELETE_COMPARISON,
  SET_ADVANCE_METRIC_SELECTION,
  SET_DEFAULT,
  EDIT_COMPARISON
} from './graphql/comparison/mutation';
import { useMemo, useRef, useState } from 'react';
import {
  GET_ADVANCE_METRIC,
  GET_BRAND_PEAK_TIME,
  GET_BRAND_POPULARITY,
  GET_BRAND_POSITIONING,
  GET_BRAND_SENTIMENT,
  GET_CHRONOLOGICAL_AUDIENCE_BY_BRAND,
  GET_CHRONOLOGICAL_ENGAGEMENT_BY_BRAND,
  GET_CHRONOLOGICAL_TALK_BY_BRAND,
  GET_COMPARISON_ENTITY,
  GET_NAMESPACE_LIST,
  GET_SHARE_OF_VIEWS,
  GET_TOP_POSTMADE_PAGINATION_V2
} from './graphql/comparison/query';
import uuidNameSpace from 'uuid/v5';
import getNS from 'uuid/v4';
import {
  IBrandPositioning,
  IComparisonEntity,
  IGroupSentiment,
  INamespace,
  IPeakTimeGroup,
  IRawAdvanceMetric,
  IShareOfVoice
} from 'src/models/comparison';
import {
  IAdvanceSearchSocialMedia,
  IChronological,
  IGranularity,
  INumericOutput,
  IRawPostmadeOutputEntity,
  IRawPostmadeOutputWithPagination
} from 'src/models/general';
import { generateGroupedBarChartData } from 'src/utils/generateChronologicalGroup';
import * as XLSX from 'xlsx';
import { projectIds } from 'src/constants/projectIds';

interface IGetListComparisonResponse {
  comparison_getComparisonNamespaceList: Array<INamespace>;
}

interface IGetComparisonEntityResponse {
  comparison_getComparisonEntity: IComparisonEntity;
}

interface IGetBrandPositioningResponse {
  comparison_getBrandPositioning: Array<IBrandPositioning>;
}

interface IGetShareOfVoiceResponse {
  comparison_getBrandPopularity: Array<IShareOfVoice>;
}

interface IGetSentimentAnalysisResponse {
  comparison_getBrandSentiment: Array<IGroupSentiment>;
}

interface IGetChronologicalEngagementResponse {
  comparison_getChronologicalEngagementByBrand: Array<IChronological>;
}

interface IGetChronologicalFollowersGrowthResponse {
  comparison_getChronologicalAudienceByBrand: Array<IChronological>;
}

interface IGetDayToDayTalkResponse {
  comparison_getChronologicalTalkByBrand: Array<IChronological>;
}

interface IGetPeakTimeResponse {
  comparison_getBrandPeakTime: IPeakTimeGroup;
}

interface IGetTopPostMadeResponse {
  comparison_getTopPostMadeWithPaginationV2: IRawPostmadeOutputWithPagination;
}

interface IGetAdvanceMetricResponse {
  comparison_getAdvanceMetric: Array<IRawAdvanceMetric>;
}

interface IGetShareOfViewsResponse {
  comparison_getShareOfViews: Array<INumericOutput>;
}

const RequestNameSpace = getNS();

const useComparison = function () {
  const client = useApolloClient();
  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.users.project);

  const isPotentialReach = useMemo(() => {
    if (!project) return;
    return Boolean(project.flags.isPotentialOutreach);
  }, [project]);

  const isShowVirality = useMemo(() => {
    if (!project) return false;
    return (
      project.id === projectIds.asuransi_astra ||
      project.id === projectIds.frutella
    );
  }, [project, projectIds]);

  const finalInitHeadCellsOutreach = useMemo(() => {
    return !isShowVirality
      ? initHeadCellsOutreach.filter((item) => item.id !== 'viralityRate')
      : initHeadCellsOutreach;
  }, [isShowVirality, initHeadCellsOutreach]);

  const finalInitHeadCells = useMemo(() => {
    return !isShowVirality
      ? initHeadCells.filter((item) => item.id !== 'viralityRate')
      : initHeadCells;
  }, [isShowVirality, initHeadCells]);

  const [headCellsAdvanceMetrics, setHeadCellAdvanceMetrics] = useState<
    Array<IHeadCellAdvanceMetrics>
  >(isPotentialReach ? finalInitHeadCellsOutreach : finalInitHeadCells);
  const [metricOrder, setMetricOrder] = useState<Array<string>>([]);
  const {
    advanceMetric: currentAdvanceMetric,
    comparisonList,
    dayToDayEngagement,
    dayToDayFollowersGrowth,
    dayToDayTalk
  } = useAppSelector((state) => state.storeComparison);

  const fetchListComparison = async function (projectId: string) {
    return await client
      .query<IGetListComparisonResponse>({
        query: GET_NAMESPACE_LIST,
        variables: {
          projectId
        }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.comparison_getComparisonNamespaceList
        };
      });
  };

  const getListComparison = async function (projectId: string) {
    const response = await fetchListComparison(projectId);
    dispatch(reducerUpdateComparisonList(response));
  };

  const fetchComparisonEntity = async function (comparisonId: string) {
    return await client
      .query<IGetComparisonEntityResponse>({
        query: GET_COMPARISON_ENTITY,
        variables: {
          comparisonId
        }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.comparison_getComparisonEntity
        };
      });
  };

  const getComparisonEntity = async function (comparisonId: string) {
    const response = await fetchComparisonEntity(comparisonId);
    dispatch(reducerUpdateComparisonEntity(response));
  };

  const lastPromiseBrand: any = useRef();
  const lastPromiseSov: any = useRef();
  const lastPromiseEngagement: any = useRef();
  const lastPromiseSentiment: any = useRef();
  const lastPromiseFollower: any = useRef();
  const lastPromiseTalk: any = useRef();
  const lastPromisePeaktime: any = useRef();
  const lastPromisePostMade: any = useRef();
  const lastPromiseAdvance: any = useRef();

  // Only primary Redux queries share cancellation IDs; period-two fetches run independently.
  const fetchBrandPositioning = async function (
    parameter: IAdvanceSearchSocialMedia,
    requestNamespace?: string
  ) {
    const resBrandPositioning =
      await client.query<IGetBrandPositioningResponse>({
        query: GET_BRAND_POSITIONING,
        variables: parameter,
        fetchPolicy: requestNamespace ? undefined : 'no-cache',
        context: {
          requestTrackerId:
            requestNamespace &&
            uuidNameSpace('GET_BRAND_POSITIONING', requestNamespace)
        }
      });
    const generateData =
      resBrandPositioning.data.comparison_getBrandPositioning.map(
        (position) => ({
          name: position.name,
          data: [[position?.positivity, position?.popularity]]
        })
      );
    return { ...resBrandPositioning, data: generateData };
  };

  const fetchShareOfVoice = async function (
    parameter: IAdvanceSearchSocialMedia,
    requestNamespace?: string
  ) {
    const resShareOfVoice = await client.query<IGetShareOfVoiceResponse>({
      query: GET_BRAND_POPULARITY,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_BRAND_POPULARITY', requestNamespace)
      }
    });
    return {
      ...resShareOfVoice,
      data: resShareOfVoice.data.comparison_getBrandPopularity
    };
  };

  const fetchShareOfView = async function (
    parameter: IAdvanceSearchSocialMedia
  ) {
    const resShareOfViews = await client.query<IGetShareOfViewsResponse>({
      query: GET_SHARE_OF_VIEWS,
      variables: parameter,
      context: {
        requestTrackerId: uuidNameSpace('GET_SHARE_OF_VIEWS', RequestNameSpace)
      }
    });
    return {
      ...resShareOfViews,
      data: resShareOfViews.data.comparison_getShareOfViews
    };
  };

  const fetchSentimentAnalysis = async function (
    parameter: IAdvanceSearchSocialMedia,
    requestNamespace?: string
  ) {
    const response = await client.query<IGetSentimentAnalysisResponse>({
      query: GET_BRAND_SENTIMENT,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_BRAND_SENTIMENT', requestNamespace)
      }
    });
    const generateData = generateGroupedBarChartData(
      response.data?.comparison_getBrandSentiment,
      ['positive', 'negative', 'neutral']
    );
    return { ...response, data: generateData };
  };

  const keyMappingChronological = function (
    chronologicals: Array<IChronological>
  ) {
    return chronologicals.map((chronological) => ({
      name: chronological.name,
      data: chronological.values.map((value) => [value.timestamp, value.value])
    }));
  };

  const fetchDayToDayEngagement = async function (
    parameter: IAdvanceSearchSocialMedia & { granularity?: IGranularity },
    requestNamespace?: string
  ) {
    const response = await client.query<IGetChronologicalEngagementResponse>({
      query: GET_CHRONOLOGICAL_ENGAGEMENT_BY_BRAND,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace(
            'GET_CHRONOLOGICAL_ENGAGEMENT_BY_BRAND',
            requestNamespace
          )
      }
    });
    const chronologicalData =
      response.data?.comparison_getChronologicalEngagementByBrand;
    const generateData = chronologicalData
      ? keyMappingChronological(chronologicalData)
      : [];
    return { ...response, data: generateData };
  };

  const fetchDayToDayFollowersGrowth = async function (
    parameter: IAdvanceSearchSocialMedia & { granularity?: IGranularity },
    requestNamespace?: string
  ) {
    const response =
      await client.query<IGetChronologicalFollowersGrowthResponse>({
        query: GET_CHRONOLOGICAL_AUDIENCE_BY_BRAND,
        variables: parameter,
        fetchPolicy: requestNamespace ? undefined : 'no-cache',
        context: {
          requestTrackerId:
            requestNamespace &&
            uuidNameSpace(
              'GET_CHRONOLOGICAL_AUDIENCE_BY_BRAND',
              requestNamespace
            )
        }
      });
    const chronologicalData =
      response.data?.comparison_getChronologicalAudienceByBrand;
    const generateData = chronologicalData
      ? keyMappingChronological(chronologicalData)
      : [];
    return { ...response, data: generateData };
  };

  const fetchDayToDayTalk = async function (
    parameter: IAdvanceSearchSocialMedia & { granularity?: IGranularity },
    requestNamespace?: string
  ) {
    const response = await client.query<IGetDayToDayTalkResponse>({
      query: GET_CHRONOLOGICAL_TALK_BY_BRAND,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_CHRONOLOGICAL_TALK_BY_BRAND', requestNamespace)
      }
    });
    const chronologicalData =
      response.data?.comparison_getChronologicalTalkByBrand;
    const generateData = chronologicalData
      ? keyMappingChronological(chronologicalData)
      : [];
    return { ...response, data: generateData };
  };

  const keyMappingBrandPeaktime = function (datas) {
    return datas.map((data) => ({
      name: data.name,
      data: data.values.map((value, index) => [index, value])
    }));
  };

  const fetchPeakTime = async function (
    parameter: IAdvanceSearchSocialMedia,
    requestNamespace?: string
  ) {
    const response = await client.query<IGetPeakTimeResponse>({
      query: GET_BRAND_PEAK_TIME,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_BRAND_PEAK_TIME', requestNamespace)
      }
    });
    const generateData = keyMappingBrandPeaktime(
      response.data?.comparison_getBrandPeakTime
    );
    return { ...response, data: generateData };
  };

  const fetchComparisonRangeCharts = (
    parameter: IAdvanceSearchSocialMedia,
    granularities: {
      engagement: IGranularity;
      followersGrowth: IGranularity;
      talk: IGranularity;
    }
  ) =>
    Promise.all([
      fetchBrandPositioning(parameter),
      fetchShareOfVoice(parameter),
      fetchShareOfView(parameter),
      fetchSentimentAnalysis(parameter),
      fetchDayToDayEngagement({
        ...parameter,
        granularity: granularities.engagement
      }),
      fetchDayToDayFollowersGrowth({
        ...parameter,
        granularity: granularities.followersGrowth
      }),
      fetchDayToDayTalk({
        ...parameter,
        granularity: granularities.talk
      }),
      fetchPeakTime(parameter),
      fetchAdvanceMetric(parameter, false),
      fetchListOfPostMade(parameter)
    ]).then(
      ([
        brandPositioning,
        shareOfVoice,
        shareOfViews,
        sentimentAnalysis,
        dayToDayEngagement,
        dayToDayFollowersGrowth,
        dayToDayTalk,
        peakTime,
        advanceMetric,
        listOfPostMade
      ]) => ({
        brandPositioning: brandPositioning.data,
        shareOfVoice: shareOfVoice.data,
        shareOfViews: shareOfViews.data,
        sentimentAnalysis: sentimentAnalysis.data,
        dayToDayEngagement: dayToDayEngagement.data,
        dayToDayFollowersGrowth: dayToDayFollowersGrowth.data,
        dayToDayTalk: dayToDayTalk.data,
        peakTime: peakTime.data,
        advanceMetric: advanceMetric.data ?? [],
        listOfPostMade: listOfPostMade.data ?? {
          result: [],
          limit: 20,
          page: 1,
          totalPages: 1,
          totalRows: 0
        }
      })
    );

  const fetchListOfPostMade = async function (
    parameter: IAdvanceSearchSocialMedia,
    requestNamespace?: string
  ) {
    const response = await client.query<IGetTopPostMadeResponse>({
      query: GET_TOP_POSTMADE_PAGINATION_V2,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_TOP_POSTMADE_PAGINATION_V2', requestNamespace)
      }
    });
    return {
      ...response,
      data: response.data?.comparison_getTopPostMadeWithPaginationV2
    };
  };

  const fetchAllListOfPostMade = async function (
    parameter: IAdvanceSearchSocialMedia
  ) {
    const response = await client.query<IGetTopPostMadeResponse>({
      query: GET_TOP_POSTMADE_PAGINATION_V2,
      variables: parameter
    });
    return {
      data: response.data?.comparison_getTopPostMadeWithPaginationV2
    };
  };

  const fetchAdvanceMetric = async function (
    parameter: IAdvanceSearchSocialMedia,
    updateMetricState = true,
    requestNamespace?: string
  ) {
    const response = await client.query<IGetAdvanceMetricResponse>({
      query: GET_ADVANCE_METRIC,
      variables: parameter,
      fetchPolicy: requestNamespace ? undefined : 'no-cache',
      context: {
        requestTrackerId:
          requestNamespace &&
          uuidNameSpace('GET_ADVANCE_METRIC', requestNamespace)
      }
    });
    if (updateMetricState) {
      setMetricOrder(
        response.data?.comparison_getAdvanceMetric[0]?.metricOrder
          ? response.data?.comparison_getAdvanceMetric[0]?.metricOrder
          : []
      );
      setHeadCellAdvanceMetrics((prevCells: Array<IHeadCellAdvanceMetrics>) =>
        prevCells.map((col) => {
          const column =
            response.data?.comparison_getAdvanceMetric[0]?.values.find(
              (val) => val.computationName === col.id
            );
          return { ...col, active: column?.isActive };
        })
      );
    }
    const mappingMetric = response.data?.comparison_getAdvanceMetric.map(
      (metric) => {
        const objectMetric = { group: metric.name };
        headCellsAdvanceMetrics.forEach((headMetric) => {
          metric.values.forEach((val) => {
            if (headMetric.id === val.computationName && val.isActive) {
              Object.assign(objectMetric, {
                [val.computationName]: val.value
              });
            }
          });
        });
        return objectMetric;
      }
    );
    return { ...response, data: mappingMetric };
  };

  const getBrandPositioning = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchBrandPositioning(parameter, RequestNameSpace);
    lastPromiseBrand.current = response;
    if (response === lastPromiseBrand.current) {
      response.then((response) => {
        dispatch(reducerUpdateBrandPosition(response));
      });
    }
  };

  const getShareOfVoice = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchShareOfVoice(parameter, RequestNameSpace);
    lastPromiseSov.current = response;
    if (response === lastPromiseSov.current) {
      response.then((response) => {
        dispatch(reducerUpdateShareOfVoice(response));
      });
    }
  };

  const getShareOfViews = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchShareOfView(parameter);
    lastPromiseSov.current = response;
    if (response === lastPromiseSov.current) {
      response.then((response) => {
        dispatch(reducerUpdateShareOfViews(response));
      });
    }
  };

  const getSentimentAnalysis = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchSentimentAnalysis(parameter, RequestNameSpace);
    lastPromiseSentiment.current = response;
    if (response === lastPromiseSentiment.current) {
      response.then((response) => {
        dispatch(reducerUpdateSentimentAnalysis(response));
      });
    }
  };

  const getDayToDayEngagement = function (
    parameter: IAdvanceSearchSocialMedia
  ) {
    dispatch(
      reducerUpdateDayToDayEngagement({ ...dayToDayEngagement, loading: true })
    );
    const response = fetchDayToDayEngagement(parameter, RequestNameSpace);
    lastPromiseEngagement.current = response;
    if (response === lastPromiseEngagement.current) {
      response.then((response) => {
        dispatch(reducerUpdateDayToDayEngagement(response));
      });
    }
  };

  const getDayToDayFollowersGrowth = function (
    parameter: IAdvanceSearchSocialMedia
  ) {
    dispatch(
      reducerUpdateDayToDayFollowersGrowth({
        ...dayToDayFollowersGrowth,
        loading: true
      })
    );
    const response = fetchDayToDayFollowersGrowth(parameter, RequestNameSpace);
    lastPromiseFollower.current = response;
    if (response === lastPromiseFollower.current) {
      response.then((response) => {
        dispatch(reducerUpdateDayToDayFollowersGrowth(response));
      });
    }
  };

  const getDayToDayTalk = function (parameter: IAdvanceSearchSocialMedia) {
    dispatch(reducerUpdateDayToDayTalk({ ...dayToDayTalk, loading: true }));
    const response = fetchDayToDayTalk(parameter, RequestNameSpace);
    lastPromiseTalk.current = response;
    if (response === lastPromiseTalk.current) {
      response.then((response) => {
        dispatch(reducerUpdateDayToDayTalk(response));
      });
    }
  };

  const getPeakTime = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchPeakTime(parameter, RequestNameSpace);
    lastPromisePeaktime.current = response;
    if (response === lastPromisePeaktime.current) {
      response.then((response) => {
        dispatch(reducerUpdatePeakTime(response));
      });
    }
  };

  const getListOfPostMade = function (parameter: IAdvanceSearchSocialMedia) {
    dispatch(reducerUpdateLoadingListOfPostMade(true));
    const response = fetchListOfPostMade(parameter, RequestNameSpace);
    lastPromisePostMade.current = response;
    if (response === lastPromisePostMade.current) {
      response.then((response) => {
        dispatch(reducerUpdateListOfPostMade(response));
      });
    }
  };

  const getAdvanceMetric = function (parameter: IAdvanceSearchSocialMedia) {
    const response = fetchAdvanceMetric(parameter, true, RequestNameSpace);
    lastPromiseAdvance.current = response;
    if (response === lastPromiseAdvance.current) {
      response.then((response) => {
        dispatch(reducerUpdateAdvanceMetric(response));
      });
    }
  };

  const getAllMetricCompatisonSosmed = (
    parameter: IAdvanceSearchSocialMedia
  ) => {
    dispatch(reducerUpdateAllLoadingComparison(true));
    getBrandPositioning(parameter);
    getShareOfVoice(parameter);
    getShareOfViews(parameter);
    getSentimentAnalysis(parameter);
    // getDayToDayFollowersGrowth(parameter);
    getPeakTime(parameter);
    // getDayToDayTalk(parameter);
    getListOfPostMade(parameter);
    getAdvanceMetric(parameter);
  };

  const cekSourceType = (groups): 'object' | 'objectGroup' | 'label' => {
    if (groups[0].objectGroup) return 'objectGroup';
    if (groups[0].objectList?.length > 0) return 'object';
    if (groups[0].labelList?.length > 0) return 'label';
  };

  const handleExportExcelAllRows = async (
    finalPayload: any,
    listOfPostMade: ApolloQueryResult<IRawPostmadeOutputWithPagination>
  ) => {
    let totalRows = listOfPostMade.data.totalRows;
    const steps = Math.ceil(totalRows / 500);
    const allPromise: Promise<{ data: IRawPostmadeOutputWithPagination }>[] =
      [];
    for (let i = 0; i < steps; i++) {
      const fetchPromise = fetchAllListOfPostMade({
        ...finalPayload,
        page: i + 1,
        limit: 500
      });
      allPromise.push(fetchPromise);
    }
    const allResponse = await Promise.all(allPromise);
    const initAllResult: IRawPostmadeOutputEntity[] = [];

    const allResult = allResponse.reduce(
      (allResult, resp) => [...allResult, ...resp.data.result],
      initAllResult
    );

    const postmadeToCsvData = allResult.map((item) => {
      return {
        ...item,
        content: item.content.replace(/"/g, '""')
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(postmadeToCsvData);
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Postmade');

    // Download file
    XLSX.writeFile(workbook, `list-postmade.xlsx`);
  };

  const [addComparison, resAddComparison] = useMutation(ADD_COMPARISON, {
    onCompleted: ({ comparison_addComparison }) => {
      const { id, namespace, displayName, createdAt, groups, colors } =
        comparison_addComparison;
      const newComparison: INamespace = {
        id,
        namespace,
        displayName,
        createdAt,
        isDefault: false,
        colors,
        sourceType: cekSourceType(groups)
      };

      dispatch(
        reducerUpdateComparisonList({
          ...comparisonList,
          data: [...comparisonList.data, newComparison]
        })
      );
    }
  });
  const [editComparison, resEditComparison] = useMutation(EDIT_COMPARISON);

  const [deleteComparison, resDeleteComparison] = useMutation(
    DELETE_COMPARISON,
    {
      onError(err) {
        console.log(err);
      }
    }
  );

  const [setDefault, responseSetDefault] = useMutation(SET_DEFAULT, {
    onError(err) {
      console.log(err);
    }
  });

  const [setComparisonTableColumn, responseComparisonTableColumn] = useMutation(
    SET_ADVANCE_METRIC_SELECTION,
    {
      onCompleted: ({ comparison_updateAdvanceMetricSelection }) => {
        setMetricOrder(
          comparison_updateAdvanceMetricSelection[0].metricOrder
            ? comparison_updateAdvanceMetricSelection[0].metricOrder
            : []
        );
        setHeadCellAdvanceMetrics((prevCells: Array<IHeadCellAdvanceMetrics>) =>
          prevCells.map((col) => {
            const column =
              comparison_updateAdvanceMetricSelection[0].values.find(
                (val) => val.computationName === col.id
              );
            return { ...col, active: column.isActive };
          })
        );
        const mappingMetric = comparison_updateAdvanceMetricSelection.map(
          (metric: any) => {
            const objectMetric = { group: metric.name };
            headCellsAdvanceMetrics.forEach((headMetric) => {
              metric.values.forEach((val) => {
                if (headMetric.id === val.computationName && val.isActive) {
                  Object.assign(objectMetric, {
                    [val.computationName]: val.value
                  });
                }
              });
            });
            return objectMetric;
          }
        );
        dispatch(
          reducerUpdateAdvanceMetric({
            ...currentAdvanceMetric,
            data: mappingMetric
          })
        );
      },
      context: {
        requestTrackerId: uuidNameSpace(
          'SET_ADVANCE_METRIC_SELECTION',
          RequestNameSpace
        )
      },
      onError(err) {
        console.log(err);
      }
    }
  );

  return {
    getListComparison,
    addComparison,
    resAddComparison,
    deleteComparison,
    resDeleteComparison,
    getAllMetricCompatisonSosmed,
    fetchComparisonRangeCharts,
    fetchDayToDayEngagement,
    fetchDayToDayFollowersGrowth,
    fetchDayToDayTalk,
    getListOfPostMade,
    fetchAllListOfPostMade,
    headCellsAdvanceMetrics,
    setHeadCellAdvanceMetrics,
    setComparisonTableColumn,
    responseComparisonTableColumn,
    setDefault,
    responseSetDefault,
    getComparisonEntity,
    editComparison,
    resEditComparison,
    getDayToDayEngagement,
    getDayToDayFollowersGrowth,
    getDayToDayTalk,
    metricOrder,
    handleExportExcelAllRows
  };
};

export default useComparison;
